import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Empty, CopyButton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [revealed, setRevealed] = useState(null);
  const load = () => api.get("/merchant/api-keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);

  const create = async (kind, mode) => {
    try { const { data } = await api.post("/merchant/api-keys", { kind, mode }); setRevealed(data); toast.success("Clé API générée"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (k) => { if (!window.confirm("Révoquer cette clé ? Les intégrations qui l'utilisent cesseront de fonctionner.")) return; await api.post(`/merchant/api-keys/${k.id}/revoke`); toast.success("Clé révoquée"); load(); };
  const regen = async (k) => { const { data } = await api.post(`/merchant/api-keys/${k.id}/regenerate`); setRevealed(data); toast.success("Clé régénérée"); load(); };

  return (
    <div>
      <PageHeader title="Clés API" subtitle="pk_ (publique) : utilisée par le widget dans le navigateur, liée à votre liste blanche de domaines. sk_ (secrète) : réservée à votre serveur, affichée une seule fois.">
        <Button variant="secondary" onClick={() => create("pk", "test")} data-testid="generate-pk-test-btn">+ pk test</Button>
        <Button variant="secondary" onClick={() => create("sk", "test")} data-testid="generate-sk-test-btn">+ sk test</Button>
        <Button className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" onClick={() => create("pk", "live")} data-testid="generate-pk-live-btn">+ pk live</Button>
        <Button className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" onClick={() => create("sk", "live")} data-testid="generate-api-key-btn">+ sk live</Button>
      </PageHeader>
      {keys.length === 0 ? <Empty text="Aucune clé. Générez une paire pk/sk en mode test pour commencer." /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-zinc-500 border-b border-border"><tr><th className="p-3">Type</th><th className="p-3">Mode</th><th className="p-3">Clé</th><th className="p-3">Créée</th><th className="p-3">Dernier usage</th><th className="p-3 text-right">Actions</th></tr></thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className={`border-b border-border/60 ${k.revoked ? "opacity-40" : ""}`} data-testid="api-key-row">
                  <td className="p-3 font-mono text-xs">{k.kind === "pk" ? "publique" : "secrète"}</td>
                  <td className="p-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${k.mode === "live" ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{k.mode}</span></td>
                  <td className="p-3 font-mono text-xs flex items-center gap-2">{k.public_key || k.prefix}{k.public_key && !k.revoked && <CopyButton value={k.public_key} label="Clé publique copiée" testId="copy-pk-button" />}</td>
                  <td className="p-3 text-xs text-zinc-500">{fmtDate(k.created_at)}</td>
                  <td className="p-3 text-xs text-zinc-500">{fmtDate(k.last_used_at)}</td>
                  <td className="p-3 text-right space-x-2">
                    {k.revoked ? <span className="text-xs text-red-400">révoquée</span> : (<>
                      <button onClick={() => regen(k)} className="text-xs text-zinc-400 hover:text-amber-400" data-testid="regenerate-key-button">Régénérer</button>
                      <button onClick={() => revoke(k)} className="text-xs text-zinc-400 hover:text-red-400" data-testid="revoke-key-button">Révoquer</button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
        <DialogContent data-testid="reveal-secret-key-modal">
          <DialogHeader>
            <DialogTitle>{revealed?.kind === "sk" ? "Clé secrète — affichée une seule fois" : "Clé publique"}</DialogTitle>
            <DialogDescription>{revealed?.kind === "sk" ? "Copiez-la maintenant et stockez-la dans les variables d'environnement de votre serveur. Elle n'est conservée que hashée (SHA-256) chez nous." : "Cette clé peut être intégrée dans le code de votre site (widget)."}</DialogDescription>
          </DialogHeader>
          <div className="mono-box flex items-center justify-between gap-2"><span data-testid="secret-key-value">{revealed?.key}</span><CopyButton value={revealed?.key} label="Clé copiée" testId="copy-secret-key-button" /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
